import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Search, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

const TYPE_FILTERS = [
  "All",
  "White Paper",
  "Research Report",
  "Methodology",
  "Article",
  "Podcast",
  "Roundtable",
  "Interview",
];

export default function KnowledgeLibrary() {
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState(null);
  const [q, setQ] = useState("");
  const [type, setType] = useState("All");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (type && type !== "All") params.asset_type = type;
      const [a, s] = await Promise.all([
        api.get("/knowledge/assets", { params }),
        api.get("/knowledge/stats"),
      ]);
      setAssets(a.data || []);
      setStats(s.data);
    } catch {
      toast.error("Unable to load ImpactVest Knowledge.");
    } finally {
      setLoading(false);
    }
  }, [q, type]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const copyCitation = (a) => {
    const cite = `${a.author || a.publisher || "ImpactVest"}${a.publication_date ? ` (${a.publication_date})` : ""}. ${a.title}. ${a.canonical_url || ""}`.trim();
    navigator.clipboard?.writeText(cite);
    toast.success("Citation copied.");
  };

  return (
    <div data-testid="knowledge-library">
      <div className="grid grid-cols-12 gap-6 mb-8">
        <div className="col-span-12 lg:col-span-8">
          <h1 className="font-serif-display text-[34px] md:text-[44px] leading-tight tracking-tight text-iv-fg">
            ImpactVest Knowledge
          </h1>
          <p className="text-[14.5px] text-iv-fg-2 mt-2 max-w-2xl">
            A research library of published methodologies, articles, interviews and roundtables —
            context for opportunity analysis and Decision Records. Sources are attributed and
            classified by citation layer.
          </p>
        </div>
        <div className="col-span-12 lg:col-span-4 flex lg:justify-end items-start gap-6">
          {stats && (
            <>
              <Stat n={stats.total} label="Published Assets" />
              <Stat n={stats.podcasts_and_roundtables} label="Podcasts & Roundtables" />
            </>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-0 top-1/2 -translate-y-1/2 text-iv-fg-3" />
          <input
            data-testid="knowledge-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search research, topics, authors…"
            className="w-full bg-transparent border-b border-iv-line-strong pl-6 py-2.5 text-[14px] text-iv-fg outline-none focus:border-iv-green"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {TYPE_FILTERS.map((t) => (
            <button
              key={t}
              onClick={() => setType(t)}
              className={`iv-chip ${type === t ? "iv-chip--gold" : ""}`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {loading ? (
        <p className="py-16 text-center font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">Loading…</p>
      ) : assets.length === 0 ? (
        <div className="py-16 text-center border border-iv-line">
          <p className="font-mono text-[12px] uppercase tracking-widest text-iv-fg-3">
            No published assets match this view.
          </p>
          <p className="text-[13px] text-iv-fg-3 mt-2">
            Approved sources appear here once a Platform Administrator publishes them to the corpus.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-iv-line border border-iv-line">
          {assets.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelected(a)}
              data-testid="knowledge-card"
              className="text-left bg-iv-surface p-5 hover:bg-white transition-colors group"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="iv-chip">{a.asset_type}</span>
                <span className="font-mono text-[10px] text-iv-fg-3">{a.publication_date || "—"}</span>
              </div>
              <h3 className="font-serif-display text-[20px] leading-snug text-iv-fg group-hover:text-iv-green transition-colors">
                {a.title}
              </h3>
              {a.summary && (
                <p className="text-[12.5px] text-iv-fg-2 mt-2 leading-relaxed line-clamp-3">{a.summary}</p>
              )}
              <div className="mt-3 font-mono text-[10px] uppercase tracking-widest text-iv-fg-3">
                {a.publisher || a.author || "ImpactVest"} · {a.citation_layer}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex" data-testid="knowledge-detail">
          <div className="flex-1 bg-black/30" onClick={() => setSelected(null)} />
          <div className="w-full max-w-xl bg-iv-surface border-l border-iv-line-strong overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-6">
              <span className="iv-chip iv-chip--gold">{selected.citation_layer}</span>
              <button onClick={() => setSelected(null)} className="font-mono text-[11px] uppercase tracking-widest text-iv-fg-3 hover:text-iv-fg">Close ✕</button>
            </div>
            <div className="font-mono-tag text-iv-fg-3">{selected.asset_type}</div>
            <h2 className="font-serif-display text-[30px] leading-tight text-iv-fg mt-2">{selected.title}</h2>
            {selected.subtitle && <p className="text-[14px] text-iv-fg-2 mt-1">{selected.subtitle}</p>}
            <div className="mt-4 font-mono text-[11px] text-iv-fg-3">
              {[selected.author, selected.publisher, selected.publication_date].filter(Boolean).join(" · ")}
            </div>
            {selected.summary && (
              <>
                <div className="font-mono-tag text-iv-fg-3 mt-8 mb-2">Executive Summary</div>
                <p className="text-[14.5px] leading-relaxed text-iv-fg">{selected.summary}</p>
              </>
            )}
            {(selected.topics?.length || selected.sectors?.length || selected.geographies?.length) ? (
              <>
                <div className="font-mono-tag text-iv-fg-3 mt-8 mb-3">Themes</div>
                <div className="flex flex-wrap gap-2">
                  {[...(selected.topics||[]), ...(selected.sectors||[]), ...(selected.geographies||[])].map((t) => (
                    <span key={t} className="iv-chip">{t}</span>
                  ))}
                </div>
              </>
            ) : null}
            <div className="mt-8 flex flex-wrap gap-3 border-t border-iv-line pt-6">
              {selected.canonical_url && (
                <a href={selected.canonical_url} target="_blank" rel="noreferrer" className="iv-btn">
                  Original Source <ExternalLink size={14} />
                </a>
              )}
              <button onClick={() => copyCitation(selected)} className="iv-btn">
                Copy Citation <Copy size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="font-serif-display text-[40px] leading-none text-iv-fg">{n}</div>
      <div className="font-mono text-[9.5px] uppercase tracking-widest text-iv-fg-3 mt-1">{label}</div>
    </div>
  );
}
